/**
 * SessionTracker – invisible sentinel component that sits inside the
 * AppDataContext.Provider and wires the SessionService to the app state.
 *
 * It watches every relevant slice of `currentAppData` and fires the
 * appropriate tracking actions whenever a value changes.
 *
 * Render it once, directly inside the Provider (see App.jsx).
 */

import { useEffect, useRef } from 'react';
import { useAppData, AppDataType } from '../../context/Contexts';
import { trackSelectedVehicle } from '../../services/KlaviyoService';
import { sessionService } from '../../services/SessionService';

// Normalise a value to a string for comparison
const str = (v: unknown): string => (v == null ? '' : String(v));

const SessionTracker: React.FC = () => {
  const { currentAppData } = useAppData();
  const prev = useRef<AppDataType | null>(null);
  const initialised = useRef(false);

  useEffect(() => {
    if (!initialised.current) {
      initialised.current = true;
      sessionService.trackAction('page_view', {
        page_url: window.location.href,
        referrer: document.referrer,
      });
    }
  }, []);

  useEffect(() => {
    if (!currentAppData) return;

    const p = prev.current;

    // -----------------------------------------------------------------------
    // Step changes
    // -----------------------------------------------------------------------
    if (currentAppData.step !== undefined && str(currentAppData.step) !== str(p?.step)) {
      const from = p?.step;
      const to = currentAppData.step;
      sessionService.updateStep(Number(to));
      sessionService.trackAction('step_change', {
        ...(from !== undefined ? { from } : {}),
        to,
      });
    }

    // -----------------------------------------------------------------------
    // Vehicle – track each field individually so we get granular events
    // -----------------------------------------------------------------------
    if (str(currentAppData.year) !== str(p?.year) && currentAppData.year) {
      sessionService.updateVehicle({ year: str(currentAppData.year) });
      sessionService.trackAction('year_selected', { year: currentAppData.year });
    }

    if (str(currentAppData.make) !== str(p?.make) && currentAppData.make) {
      sessionService.updateVehicle({ make: currentAppData.make });
      sessionService.trackAction('make_selected', { make: currentAppData.make });
    }

    if (str(currentAppData.model) !== str(p?.model) && currentAppData.model) {
      sessionService.updateVehicle({ model: currentAppData.model });
      sessionService.trackAction('model_selected', { model: currentAppData.model });
    }

    const year = str(currentAppData.year);
    const make = str(currentAppData.make);
    const model = str(currentAppData.model);
    const trim = str(currentAppData.trim);
    const cab = str(currentAppData.cab);
    const ymmtcKey = [year, make, model, trim, cab].join('|');
    const prevYmmtcKey = [str(p?.year), str(p?.make), str(p?.model), str(p?.trim), str(p?.cab)].join('|');

    if (ymmtcKey !== prevYmmtcKey) {
      trackSelectedVehicle({
        year,
        make,
        model,
        trim: trim || undefined,
        cab: cab || undefined,
        has_trims: currentAppData.has_trims,
        has_cabs: currentAppData.has_cabs,
      });
    }

    if (str(currentAppData.trim) !== str(p?.trim) && currentAppData.trim) {
      sessionService.updateVehicle({ trim: currentAppData.trim });
      sessionService.trackAction('trim_selected', { trim: currentAppData.trim });
    }

    if (str(currentAppData.cab) !== str(p?.cab) && currentAppData.cab) {
      sessionService.updateVehicle({ cab: currentAppData.cab });
      sessionService.trackAction('cab_selected', { cab: currentAppData.cab });
    }

    // -----------------------------------------------------------------------
    // Product selection
    // -----------------------------------------------------------------------
    if (str(currentAppData.seat) !== str(p?.seat) && currentAppData.seat) {
      sessionService.updateProduct({ seat: currentAppData.seat });
      sessionService.trackAction('seat_selected', { seat: currentAppData.seat });
    }

    if (str(currentAppData.color) !== str(p?.color) && currentAppData.color) {
      sessionService.updateProduct({ color: currentAppData.color });
      sessionService.trackAction('color_selected', { color: currentAppData.color });
    }

    if (str(currentAppData.selectedId) !== str(p?.selectedId) && currentAppData.selectedId) {
      sessionService.updateProduct({ selected_variant_id: currentAppData.selectedId });
    }

    // -----------------------------------------------------------------------
    // Compatibility check result
    // -----------------------------------------------------------------------
    if (currentAppData.check !== p?.check && currentAppData.check) {
      sessionService.trackAction('compatibility_checked', {
        compatibility: currentAppData.check.compatibility,
        compatibility_custom: currentAppData.check.compatibility_custom,
      });
    }

    // -----------------------------------------------------------------------
    // Configurator completed
    // -----------------------------------------------------------------------
    if (currentAppData.configurator_complete && !p?.configurator_complete) {
      sessionService.setConfiguratorComplete(true);
      sessionService.trackAction('configurator_completed', {
        year: currentAppData.year,
        make: currentAppData.make,
        model: currentAppData.model,
        seat: currentAppData.seat,
        color: currentAppData.color,
        selected_variant_id: currentAppData.selectedId,
      });
    }

    prev.current = currentAppData;
  }, [currentAppData]);

  return null;
};

export default SessionTracker;
